import { Injectable } from '@angular/core';
import { PrivateAsset, SellAsset } from './private-asset.model';
import { catchError, filter, switchMap, switchMapTo, take, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { UiService } from '../../../../shared/state/ui/ui.service';
import { PrivateAssetStore } from './private-asset.store';
import { PrivateAssetQuery } from './private-asset.query';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { applyTransaction } from '@datorama/akita';
import { SessionQuery } from '../../../../core/state/session/session.query';
import parseDataURL from 'data-urls';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { UserSettingsQuery } from 'src/app/features/user-settings/state/user-settings.query';

const cacheBusterFetchPrivateAssets$ = new BehaviorSubject(null);
const MAX_AGE_FOR_CACHING = 60 * 1000 * 5; //5 minutes - decided at the frontend meeting

@Injectable({ providedIn: 'root' })
export class PrivateAssetService {
    constructor(
        private http: HttpClient,
        private config: AppConfigQuery,
        private uiService: UiService,
        private privateAssetStore: PrivateAssetStore,
        private sessionQuery: SessionQuery,
        private userSettingsQuery: UserSettingsQuery
    ) {}

    /**
     * Initializes the Private Assets service by fetching private assets when authenticated.
     * Designed as part of a lazy loading architecture,
     * this function should be invoked prior to accessing the private assets list.
     * It also incorporates cache management to optimize data retrieval.
     */
    public getPrivateAssetsIfAuthenticated() {
        this.sessionQuery.isAuthenticated$
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                take(1),
                switchMapTo(this.getPrivateAssetsWithBaseCurrency())
            )
            .subscribe();
    }

    //region Fetching Data

    /**
     * Fetches a private asset and upserts into the local private asset store.
     * Uses the base currency currently saved in settings.
     *
     * @param instrumentId
     */
    fetchPrivateAsset(instrumentId: number) {
        return this.http
            .get<PrivateAsset>(
                `${this.config.get().privateAssetBaseUrl}/v1_0/assets/${instrumentId}`,
                {
                    params: {
                        currency: this.userSettingsQuery.getGlobalSetting('BASE_CURRENCY')
                    }
                }
            )
            .pipe(
                tap((privateAsset) => {
                    this.privateAssetStore.upsert(instrumentId, privateAsset);
                })
            );
    }

    // @Cacheable({
    //     async: true,
    //     maxAge: MAX_AGE_FOR_CACHING,
    //     cacheBusterObserver: cacheBusterFetchPrivateAssets$,
    //     maxCacheCount: 10
    // })
    fetchPrivateAssets(currency?: string): Observable<PrivateAsset[]> {
        return this.http.get<PrivateAsset[]>(
            `${this.config.get().privateAssetBaseUrl}/v1_0/assets`,
            {
                params: {
                    currency:
                        currency ||
                        this.userSettingsQuery.getGlobalSetting('BASE_CURRENCY')
                }
            }
        );
    }

    //endregion

    //region Asset Management
    upsertAsset(asset: any, id?: number): Observable<PrivateAsset> {
        if (id) {
            return this.http
                .put<number>(
                    `${this.config.get().privateAssetBaseUrl}/v1_0/assets/${id}`,
                    asset
                )
                .pipe(
                    switchMap(() => this.updateStore(id, true)),
                    catchError((err) => {
                        console.error('Error at putting private asset ${id}', err);
                        return throwError(err);
                    })
                );
        } else {
            return this.http
                .post<number>(
                    `${this.config.get().privateAssetBaseUrl}/v1_0/assets`,
                    asset
                )
                .pipe(switchMap((newId) => this.updateStore(newId, false)));
        }
    }

    updateStore(id: number, update: boolean): Observable<PrivateAsset> {
        return this.http
            .get<PrivateAsset>(
                `${this.config.get().privateAssetBaseUrl}/v1_0/assets/${id}`,
                {
                    params: {
                        currency: this.userSettingsQuery.getGlobalSetting('BASE_CURRENCY')
                    }
                }
            )
            .pipe(
                tap((privateAsset) => {
                    if (update) {
                        this.privateAssetStore.update(id, privateAsset);
                    } else {
                        this.privateAssetStore.add(privateAsset);
                    }
                }),
                catchError((err) => {
                    console.error('Error at getting private asset ${id}', err);
                    return throwError(err);
                })
            );
    }

    // @CacheBuster({
    //     cacheBusterNotifier: cacheBusterFetchPrivateAssets$
    // })
    deleteAsset(instrumentId: number) {
        return this.http
            .delete<PrivateAsset>(
                `${
                    this.config.get().privateAssetBaseUrl
                }/v1_0/assets/delete/${instrumentId}`
            )
            .pipe(
                tap((privateAsset) => {
                    this.privateAssetStore.update(instrumentId, privateAsset);
                    this.privateAssetStore.remove(instrumentId);
                })
            );
    }

    // @CacheBuster({
    //     cacheBusterNotifier: cacheBusterFetchPrivateAssets$
    // })
    sellAsset(sellAsset: SellAsset) {
        return this.http
            .post<PrivateAsset>(
                `${this.config.get().privateAssetBaseUrl}/v1_0/assets/sell`,
                sellAsset
            )
            .pipe(
                tap((privateAsset) => {
                    this.privateAssetStore.update(sellAsset.instrumentId, privateAsset);
                })
            );
    }

    //endregion

    //region UI and State Management

    setActiveAssetType(activeAssetType: string) {
        this.privateAssetStore.update((state) => ({ ...state, activeAssetType }));
    }

    //endregion

    //region Asset Image Handling

    @Cacheable({
        async: true,
        maxAge: 60000,
        cacheBusterObserver: cacheBusterFetchPrivateAssets$
    })
    readImage(id: number) {
        return this.http.get<string>(
            `${this.config.get().privateAssetBaseUrl}/v1_0/assets/read_image/${id}`
        );
    }

    @CacheBuster({
        cacheBusterNotifier: cacheBusterFetchPrivateAssets$
    })
    saveImage(id: number, base64: parseDataURL.DataURL) {
        const body = {
            data: base64
        };

        return this.http.put<number>(
            `${this.config.get().privateAssetBaseUrl}/v1_0/assets/save_image/${id}`,
            body
        );
    }

    //endregion

    //region Helpers
    getPrivateAssetsWithBaseCurrency() {
        return of(this.userSettingsQuery.getGlobalSetting('BASE_CURRENCY')).pipe(
            switchMap((currency) =>
                this.fetchPrivateAssets(currency).pipe(
                    tap((entities) => {
                        applyTransaction(() => {
                            this.privateAssetStore.upsertMany(entities);
                        });
                    })
                )
            )
        );
    }
    //endregion
}
